import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ref, get } from "firebase/database";
import { database } from "../../../firebaseConfig";
import "./ValueBasedOffer.css";
import "./ValueBasedOfferPublic.css";

const formatPrice = (price, currency = "EUR") => {
  const n = Number(price) || 0;
  try {
    return new Intl.NumberFormat("de-DE", {
      style: "currency",
      currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(n);
  } catch (_) {
    return `${n} ${currency}`;
  }
};

/**
 * Public, read-only renderer for a saved value-based offer.
 *
 * Loads from /publicValueOffers/{userId+offerId}. Uses the same .vbo-view__*
 * styling as the internal editor, but strips all controls, the topbar and the
 * "Value-Based Offer" eyebrow so the page looks like a clean, shareable
 * proposal document.
 */
const ValueBasedOfferPublic = () => {
  const { customId } = useParams();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      if (!customId) {
        setError("Ungültiger Link");
        setLoading(false);
        return;
      }
      try {
        const snap = await get(ref(database, `/publicValueOffers/${customId}`));
        if (!snap.exists()) {
          setError("Angebot nicht gefunden oder nicht öffentlich freigegeben.");
          setLoading(false);
          return;
        }
        setOffer(snap.val());
      } catch (e) {
        console.error("Error loading public value offer:", e);
        setError("Fehler beim Laden des Angebots.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [customId]);

  if (loading) {
    return (
      <div className="vbo-public__page">
        <div className="vbo-public__state">Lade Angebot…</div>
      </div>
    );
  }

  if (error || !offer || !offer.generated) {
    return (
      <div className="vbo-public__page">
        <div className="vbo-public__state vbo-public__state--error">
          {error || "Angebot konnte nicht geladen werden."}
        </div>
      </div>
    );
  }

  const g = offer.generated;
  const docTitle = g.title || "Angebot";

  return (
    <div className="vbo-public__page">
      <Helmet>
        <title>{docTitle}</title>
        <meta property="og:title" content={docTitle} />
        {g.introText && (
          <meta
            name="description"
            content={String(g.introText).replace(/<[^>]+>/g, "").slice(0, 200)}
          />
        )}
      </Helmet>

      <div className="vbo-view vbo-view--public">
        <article className="vbo-view__doc">
          {/* Letter-style head: BonusClub logo centered, recipient block left */}
          <header className="vbo-view__letterhead">
            <div className="vbo-view__letterhead-brand">
              <img
                src="/img/Bonusclub_Icon.svg"
                alt="BonusClub"
                className="vbo-view__letterhead-logo"
              />
              <span className="vbo-view__letterhead-brand-name">
                BonusClub
              </span>
            </div>
            {/* Address group: bundles "Rücksendeadresse" sender line and
                the recipient (Anschriftenfeld) into one flex child so the
                outer letterhead gap (52px) only sits between the brand and
                this group. Inside the group sender → recipient stay tight. */}
            <div className="vbo-view__letterhead-address">
            {(() => {
              const m = g.signature?.teamMember || null;
              if (!m) return null;
              const company = (m.companyName || "").trim();
              const repName = (m.name || "").trim();
              if (!company && !repName) return null;
              return (
                <div className="vbo-view__letterhead-sender">
                  {company && <strong>{company}</strong>}
                  {company && repName && (
                    <span
                      className="vbo-view__letterhead-sender-sep"
                      aria-hidden="true"
                    >
                      ·
                    </span>
                  )}
                  {repName && (
                    <span className="vbo-view__letterhead-sender-rep">
                      {repName}
                    </span>
                  )}
                  <span
                    className="vbo-view__letterhead-sender-sep"
                    aria-hidden="true"
                  >
                    ·
                  </span>
                  <span>bonusclub.io</span>
                </div>
              );
            })()}
            {(() => {
              const r = g.recipient || {};
              // Resolve the single addressee shown as "z.Hd." — same logic
              // as the editor in OfferView so public + internal stay in sync.
              const mode = r.addresseeMode === "legal" ? "legal" : "meeting";
              const addresseeName =
                mode === "legal"
                  ? r.legalRepresentative || r.invoiceRecipient || ""
                  : r.invoiceRecipient || r.legalRepresentative || "";
              if (
                !r.companyName &&
                !addresseeName &&
                !r.companyAddress
              ) {
                return null;
              }
              return (
                <div className="vbo-view__letterhead-recipient">
                  {r.companyName && (
                    <div className="vbo-view__letterhead-recipient-line vbo-view__letterhead-recipient-line--strong">
                      {r.companyName}
                    </div>
                  )}
                  {addresseeName && (
                    <div className="vbo-view__letterhead-recipient-line">
                      <span className="vbo-view__letterhead-label">
                        z.Hd.&nbsp;
                      </span>
                      {addresseeName}
                    </div>
                  )}
                  {r.companyAddress &&
                    r.companyAddress
                      .split("\n")
                      .map((l) => l.trim())
                      .filter(Boolean)
                      .filter(
                        (l) =>
                          l.toLowerCase() !==
                          String(r.companyName || "")
                            .trim()
                            .toLowerCase()
                      )
                      .map((line, i) => (
                        <div
                          key={i}
                          className="vbo-view__letterhead-recipient-line"
                        >
                          {line}
                        </div>
                      ))}
                </div>
              );
            })()}
            </div>
          </header>

          <header className="vbo-view__hero">
            <h1 className="vbo-view__title">{g.title || ""}</h1>
          </header>

          {g.introText && (
            <section className="vbo-view__intro">
              <div
                className="vbo-view__rich"
                dangerouslySetInnerHTML={{ __html: g.introText }}
              />
            </section>
          )}

          {(g.sections || []).map((sec, idx) => (
            <div className="vbo-view__section" key={sec.id || idx}>
              <div className="vbo-view__section-head">
                <h2 className="vbo-view__section-title">{sec.heading}</h2>
              </div>
              <div
                className="vbo-view__rich"
                dangerouslySetInnerHTML={{ __html: sec.html || "" }}
              />
            </div>
          ))}

          {g.leistungsumfang && (
            <section className="vbo-view__leist">
              <div className="vbo-view__section-head">
                <h2 className="vbo-view__section-title">
                  {g.leistungsumfang.heading || "Leistungsumfang"}
                </h2>
              </div>
              {g.leistungsumfang.description && (
                <div
                  className="vbo-view__rich vbo-view__leist-desc"
                  dangerouslySetInnerHTML={{
                    __html: g.leistungsumfang.description,
                  }}
                />
              )}

              {Array.isArray(g.leistungsumfang.setupPackages) &&
                g.leistungsumfang.setupPackages.length > 0 && (
                  <div className="vbo-view__setup-section">
                    <div className="vbo-view__setup-eyebrow">Setup-Pakete</div>
                    <div
                      className={`vbo-view__setup-grid vbo-view__setup-grid--${g.leistungsumfang.setupPackages.length}`}
                    >
                      {g.leistungsumfang.setupPackages.map((pkg, pkgIdx) => (
                        <div
                          className={`vbo-view__setup-card ${
                            pkg.price === 0 ? "is-included" : "is-addon"
                          }`}
                          key={pkg.id || pkgIdx}
                        >
                          <div className="vbo-view__setup-card-head">
                            <strong className="vbo-view__setup-card-title">
                              {pkg.title}
                            </strong>
                            <span className="vbo-view__setup-card-price">
                              {pkg.price === 0
                                ? "im Monatspreis enthalten"
                                : `${formatPrice(
                                    pkg.price,
                                    pkg.currency
                                  )} einmalig`}
                            </span>
                          </div>
                          {pkg.description && (
                            <div
                              className="vbo-view__setup-card-desc"
                              dangerouslySetInnerHTML={{
                                __html: pkg.description,
                              }}
                            />
                          )}
                          <ul className="vbo-view__setup-card-bullets">
                            {(pkg.bullets || []).map((b, bIdx) => (
                              <li key={bIdx}>
                                <span className="vbo-view__bullet-mark">✓</span>
                                {b}
                              </li>
                            ))}
                          </ul>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

              <div
                className={`vbo-view__options vbo-view__options--${(g.leistungsumfang.options || []).length}`}
              >
                {(g.leistungsumfang.options || []).map((opt, optIdx) => {
                  const optKey = opt.id || `idx-${optIdx}`;
                  const selectedKey = g.leistungsumfang.selectedOptionId || null;
                  const isSelected = selectedKey === optKey;
                  const isDeselected = !!selectedKey && !isSelected;
                  return (
                  <div
                    className={`vbo-view__option${
                      isSelected ? " is-selected" : ""
                    }${isDeselected ? " is-deselected" : ""}`}
                    key={opt.id || optIdx}
                  >
                    {isSelected && (
                      <div className="vbo-view__option-check" aria-hidden="true">
                        ✓
                      </div>
                    )}
                    {opt.recommendationBadge && !selectedKey && (
                      <div className="vbo-view__option-badge">
                        {opt.recommendationBadge}
                      </div>
                    )}
                    <div className="vbo-view__option-head">
                      <div className="vbo-view__option-title">{opt.title}</div>
                      {opt.subtitle && (
                        <div className="vbo-view__option-subtitle">
                          {opt.subtitle}
                        </div>
                      )}
                      <div className="vbo-view__option-price">
                        {formatPrice(opt.price, opt.currency)}
                        {Number(opt.setupFee) > 0 && (
                          <div className="vbo-view__option-setup">
                            + einmalig {formatPrice(opt.setupFee, opt.currency)}{" "}
                            Setup
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="vbo-view__chapters">
                      {opt.inheritsLabel && (
                        <div
                          className="vbo-view__inherits"
                          title="Alle Inhalte der vorherigen Pakete sind hier ebenfalls enthalten."
                        >
                          <span className="vbo-view__inherits-icon" aria-hidden="true">
                            ✓
                          </span>
                          <span className="vbo-view__inherits-text">
                            {opt.inheritsLabel}
                          </span>
                        </div>
                      )}
                      {(opt.chapters || []).map((chp, chpIdx) => (
                        <div className="vbo-view__chapter" key={chp.id || chpIdx}>
                          <div className="vbo-view__chapter-head">
                            <strong className="vbo-view__chapter-title">
                              {chp.title}
                            </strong>
                          </div>

                          <div className="vbo-view__modules">
                            {(chp.modules || []).map((mod, modIdx) => (
                              <div
                                className="vbo-view__module"
                                key={mod.id || modIdx}
                              >
                                <div className="vbo-view__module-head">
                                  <strong className="vbo-view__module-title">
                                    {mod.title}
                                  </strong>
                                </div>
                                {mod.text && (
                                  <div
                                    className="vbo-view__module-text vbo-view__rich"
                                    dangerouslySetInnerHTML={{ __html: mod.text }}
                                  />
                                )}
                                {Array.isArray(mod.bullets) &&
                                  mod.bullets.length > 0 && (
                                    <ul className="vbo-view__bullets">
                                      {mod.bullets.map((b, i) => (
                                        <li key={i} className="vbo-view__bullet">
                                          <span className="vbo-view__bullet-mark">
                                            ✓
                                          </span>
                                          <span>{b}</span>
                                        </li>
                                      ))}
                                    </ul>
                                  )}
                              </div>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  );
                })}
              </div>
            </section>
          )}

          {(Array.isArray(g.terms?.blocks)
            ? g.terms.blocks.length > 0
            : g.terms?.html) || g.terms?.validUntil ? (
            <section className="vbo-view__terms">
              <div className="vbo-view__section-head">
                <h2 className="vbo-view__section-title">Vertragsbedingungen</h2>
              </div>

              <div className="vbo-view__terms-list">
                {Array.isArray(g.terms?.blocks) && g.terms.blocks.length > 0 ? (
                  g.terms.blocks.map((block, idx) => (
                    <div
                      className="vbo-view__term-block"
                      key={block.id || idx}
                    >
                      {block.title && (
                        <div className="vbo-view__term-block-head">
                          <strong className="vbo-view__term-block-title">
                            {block.title}
                          </strong>
                        </div>
                      )}
                      <div
                        className="vbo-view__rich"
                        dangerouslySetInnerHTML={{ __html: block.html || "" }}
                      />
                    </div>
                  ))
                ) : g.terms?.html ? (
                  <div
                    className="vbo-view__rich"
                    dangerouslySetInnerHTML={{ __html: g.terms.html }}
                  />
                ) : null}
              </div>

              <div className="vbo-view__terms-footnote">
                Entspricht branchenüblichen B2B-Standards. Bei Rückfragen zu
                einzelnen Klauseln sind wir gerne direkt erreichbar.
              </div>

              {g.terms?.validUntil && (
                <div className="vbo-view__terms-validity">
                  Das Angebot ist gültig bis zum {g.terms.validUntil}.
                </div>
              )}
            </section>
          ) : null}

          {(g.signature?.teamMember ||
            g.recipient?.invoiceRecipient ||
            g.recipient?.companyName) && (
            <section className="vbo-view__signature">
              <div className="vbo-view__signature-pair">
                {/* Left: our representative — only render if a team member
                    was selected, otherwise leave the column blank so the
                    customer block on the right stays aligned. */}
                <div className="vbo-view__signature-card">
                  {g.signature?.teamMember && (
                    <>
                      {g.signature.teamMember.signature && (
                        <img
                          src={g.signature.teamMember.signature}
                          alt={`Unterschrift ${g.signature.teamMember.name}`}
                          className="vbo-view__signature-image"
                          style={{
                            maxWidth: `${
                              g.signature.signatureMaxWidth || 80
                            }px`,
                          }}
                        />
                      )}
                      <div className="vbo-view__signature-line" />
                      <div className="vbo-view__signature-meta">
                        <div className="vbo-view__signature-name">
                          {g.signature.teamMember.name}
                        </div>
                        {g.signature.teamMember.role && (
                          <div className="vbo-view__signature-role">
                            {g.signature.teamMember.role}
                          </div>
                        )}
                        {g.signature.teamMember.companyName && (
                          <div className="vbo-view__signature-company">
                            {g.signature.teamMember.companyName}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Right: customer signature line. The customer signs on
                    print/PDF — we leave empty space above the line and
                    print their name + company below. */}
                <div className="vbo-view__signature-card vbo-view__signature-card--customer">
                  <div className="vbo-view__signature-empty-space" />
                  <div className="vbo-view__signature-line" />
                  {/* Customer-side meta. The salesperson can override
                      `signatureName` / `signatureCompany` via the inline
                      editor in the dashboard — those overrides win over
                      the auto-fill from invoiceRecipient / companyName. */}
                  {(() => {
                    const r = g.recipient || {};
                    const mode =
                      r.addresseeMode === "legal" ? "legal" : "meeting";
                    const fallbackName =
                      mode === "legal"
                        ? r.legalRepresentative || r.invoiceRecipient || ""
                        : r.invoiceRecipient || r.legalRepresentative || "";
                    const sigName =
                      r.signatureName ?? fallbackName;
                    const sigCompany =
                      r.signatureCompany ?? r.companyName ?? "";
                    if (!sigName && !sigCompany) return null;
                    return (
                      <div className="vbo-view__signature-meta">
                        {sigName && (
                          <div className="vbo-view__signature-name">
                            {sigName}
                          </div>
                        )}
                        {sigCompany && (
                          <div className="vbo-view__signature-company">
                            {sigCompany}
                          </div>
                        )}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </section>
          )}

          {/* Sender "Briefkopf" footer — pulled exclusively from the
              snapshotted team member. Renders nothing when no member was
              chosen (no fallback / placeholder data — by design). */}
          {(() => {
            const m = g.signature?.teamMember;
            if (!m) return null;
            const hasCompany = !!(
              m.companyName ||
              m.billingStreet ||
              m.billingZip ||
              m.billingCity ||
              m.billingCountry ||
              m.phone
            );
            const hasLegal = !!(
              m.ceo ||
              m.tradeRegister ||
              m.vatId ||
              m.taxNumber
            );
            const hasBank = !!(m.bankName || m.iban || m.bic);
            if (!hasCompany && !hasLegal && !hasBank) return null;
            return (
              <footer className="vbo-view__sender-footer">
                {hasCompany && (
                  <div className="vbo-view__sender-col">
                    {m.companyName && (
                      <div className="vbo-view__sender-strong">
                        {m.companyName}
                      </div>
                    )}
                    {m.billingStreet && <div>{m.billingStreet}</div>}
                    {(m.billingZip || m.billingCity) && (
                      <div>
                        {m.billingZip} {m.billingCity}
                      </div>
                    )}
                    {m.billingCountry && <div>{m.billingCountry}</div>}
                    {m.phone && <div>TEL. {m.phone}</div>}
                  </div>
                )}
                {hasLegal && (
                  <div className="vbo-view__sender-col">
                    {m.ceo && (
                      <>
                        <div className="vbo-view__sender-strong">
                          Geschäftsführer:
                        </div>
                        <div>{m.ceo}</div>
                      </>
                    )}
                    {m.tradeRegister && <div>{m.tradeRegister}</div>}
                    {m.vatId && <div>USt-IdNr.: {m.vatId}</div>}
                    {m.taxNumber && <div>Steuernummer: {m.taxNumber}</div>}
                  </div>
                )}
                {hasBank && (
                  <div className="vbo-view__sender-col">
                    {m.bankName && (
                      <div className="vbo-view__sender-strong">
                        {m.bankName}
                      </div>
                    )}
                    {m.bic && <div>BIC: {m.bic}</div>}
                    {m.iban && <div>IBAN: {m.iban}</div>}
                  </div>
                )}
              </footer>
            );
          })()}
        </article>
      </div>
    </div>
  );
};

export default ValueBasedOfferPublic;
